<script lang="ts">
import {
  defineComponent,
  PropType,
  ref,
  computed,
  watch,
  nextTick,
  onBeforeUnmount,
  toRef,
  inject,
} from 'vue'
import debounce from 'lodash.debounce'
import throttle from 'lodash/throttle'
import { VALIDATION_DEFAULT_MESSAGES } from '@/constants/validationMessages'
import { COMPANY, EMPLOYEE_NUM } from '@/_core/endpoint'
import apiRequestManager from '@/_core/apiRequestManager'
import AccordionBody from '@/components/accordion/AccordionBody.vue'
import InputDefault from '@/components/form/InputDefault.vue'
import CompanySearchGuidePanel from '@/components/lead/CompanySearchGuidePanel.vue'
import CompanySearchResultList from '@/components/lead/CompanySearchResultList.vue'
import backdrop from '@/models/backdrop'
import { formDefaultProps, formPartial } from '@/models/formPartial'
import { useScrollToHash } from '@/composables/useScrollToHash'
import { VALIDATION_OBSERVER_KEY } from '@/components/validation/context'
import { extractCorporateNumber } from '@/utilities/companySearchQuery'
import { formatHiraganaToKatakana, isHiraganaOnly } from '@/utilities/formatter'
import scrollUtil from '@/utilities/scrollUtil'
import type { Ref } from 'vue'

/**
 * 会社名入力欄の下に何を出しているかという表示状態
 * - idle: 何も出していない
 * - suggest: 入力欄直下に浮動サジェスト（候補あり・フォーカス中）
 * - inlineResults: 候補があるのに選ばずに離脱した／チップ検索で候補が出た → インラインに候補一覧
 * - guide: 候補 0 件・検索 API エラー・ユーザー操作 → 会社を探すための案内パネル
 */
type SearchResultViewCategory = 'idle' | 'suggest' | 'inlineResults' | 'guide'

export default defineComponent({
  name: 'InputCompanyName',

  components: {
    AccordionBody,
    InputDefault,
    CompanySearchGuidePanel,
    CompanySearchResultList,
  },

  props: {
    ...formDefaultProps,

    modelValue: {
      type: String,
      required: true,
    },

    corporateZipcode: {
      type: Object as PropType<InputText>,
      required: true,
    },

    corporateAddress1: {
      type: Object as PropType<InputText>,
      required: true,
    },

    corporateAddress2: {
      type: Object as PropType<InputText>,
      required: true,
    },

    corporateNumber: {
      type: Object as PropType<InputText>,
      required: true,
    },

    corporateEmployeeNum: {
      type: Object as PropType<InputNumber>,
      required: true,
    },

    fieldId: {
      type: String,
      default: ''
    },

    header: {
      type: String,
      default: '勤務先名',
    },

    placeholder: {
      type: String,
      default: '例：株式会社ビズヒント',
    },

    /**
     * autocomplete 属性に渡す値
     * @see https://developer.mozilla.org/ja/docs/Web/HTML/Attributes/autocomplete
     */
    autocomplete: {
      type: String as PropType<AutocompleteAttr>,
      default: 'organization',
    },

    maxlength: {
      type: Number,
      default: undefined,
    },

    /**
     * フォームのフォーカスアウト時に前後ホワイトスペースの trim するかどうか
     */
    hasTrim: {
      type: Boolean,
      default: true,
    },
  },

  emits: ['update:modelValue', 'select:companyLeadInfo', 'error'],

  setup(props, { emit }) {
    const BACKDROP_CANCEL_ID = 'companySearchInfo' as const
    // 候補に無い会社として登録するときに法人番号へ入れる便宜上の値。後続処理（macau・BigQuery）が参照するため変えない
    const EXCEPT_OPTION = {
      VALUE: '未設定',
    } as const
    const COMPANY_INFO_REQUIRED_MESSAGE = '正確な企業情報に基づくサービスをご提供するため、お手数ですが入力内容を一度消去し、会社名を再度入力のうえ表示された候補からご選択ください。この作業は初回のみで、以降のコンテンツ閲覧がよりスムーズになります' as const
    const UNSELECTED_HEADING = '候補から選んでください' as const

    const { scrollToHash } = useScrollToHash()

    // 自身が属するバリデーション区画（検査開始のタイミングを知るために参照する）
    const validationObserver = inject(VALIDATION_OBSERVER_KEY, undefined)

    const { hasError, isRequired, createErrorMessage } = formPartial(props.name, toRef(props, 'validationScheme'))

    const suggestPositionStyle = ref('')
    const rootEl = ref()
    const inputEl = ref()

    /**
     * 案内パネルのスクロール先 ID
     *
     * @description
     *  スクロールは scrollToHash（ヘッダー分の offset 調整込み）に任せるため ID で指す必要がある。
     *  この入力欄は 1 画面に複数並び得るので、インスタンスごとに一意にする
     */
    const guidePanelId = `company-search-guide-${Math.random().toString(36).substring(2, 15)}`
    const isFocus = ref(false)

    /**
     * 入力欄の下に何を出しているか
     */
    const searchResultViewCategory: Ref<SearchResultViewCategory> = ref('idle')

    /**
     * インライン候補一覧の見出し（inlineResults のときだけ意味を持つ）
     */
    const inlineHeading = ref('')

    /**
     * 案内パネルを開いた理由（guide のときだけ意味を持つ）
     */
    const searchGuideCause: Ref<CompanySearchGuideCause> = ref('no_results')

    /**
     * 法人番号照会の状態（guide のときだけ表示に使う）
     */
    const corporateNumberLookupStatus: Ref<CorporateNumberLookupStatus> = ref('idle')

    /**
     * 表示を切り替える
     *
     * @description
     *  表示状態と、それに付随する値（見出し・開いた理由・照会状態）は必ずこの 4 つの関数だけで更新する。
     *  呼び出し側で個別に代入すると、状態を変えたときに関係する値のリセットを忘れて
     *  前の表示の値が残る（見出しが古いまま・照会中の表示が固着する）ため
     */
    const showIdle = () => {
      searchResultViewCategory.value = 'idle'
      resetViewPayload()
    }

    const showSuggest = () => {
      searchResultViewCategory.value = 'suggest'
      resetViewPayload()
    }

    const showInlineResults = (heading: string) => {
      searchResultViewCategory.value = 'inlineResults'
      resetViewPayload()
      inlineHeading.value = heading
    }

    const showGuide = (cause: CompanySearchGuideCause, status: CorporateNumberLookupStatus = 'idle') => {
      searchResultViewCategory.value = 'guide'
      resetViewPayload()
      searchGuideCause.value = cause
      corporateNumberLookupStatus.value = status
    }

    /**
     * 直前のクエリに紐づく値（見出し・法人番号照会の状態）を既定値に戻す
     *
     * @description
     *  新しい検索・照会を始めるときにも使う。新しい結果が返るまでのちらつきを避けるため、
     *  表示状態そのものは変えず、付随する値だけを戻す。
     *  見出しを空にせず既定文言を入れるのは、入力を変えた直後もインライン一覧を表示したままにするため。
     *  無効にした法人番号照会のコールバックは世代ガードで loading を解除できないため、ここで解除する役割も兼ねる。
     *
     *  searchGuideCause は戻さない。案内を開いた理由は明示的に開くときにだけ決まる値で、
     *  showGuide() が必ず設定するため。ここで既定値に戻すと、案内を出したまま
     *  searchCompanies が早期 return したとき（再検索チップ経由で古いレスポンスになった、
     *  会社が確定した、入力が消えた）に、開いた理由だけが書き換わって残ってしまう
     */
    const resetViewPayload = () => {
      inlineHeading.value = UNSELECTED_HEADING
      corporateNumberLookupStatus.value = 'idle'
    }

    /**
     * 法人番号照会の状態を差し替える
     *
     * @description 照会の状況は見つからないときパネルの中にしか出さないため、パネルが閉じているときは何もしない
     */
    const updateCorporateNumberLookupStatus = (status: CorporateNumberLookupStatus) => {
      if (searchResultViewCategory.value !== 'guide') return

      corporateNumberLookupStatus.value = status
    }

    /**
     * 法人番号が見つからなかったことを示す
     *
     * @description パネルが閉じていれば開く。既に開いているときは開いた原因（接続エラー・ユーザー操作）をそのまま保つ
     */
    const showCorporateNumberNotFound = () => {
      const cause = searchResultViewCategory.value === 'guide' ? searchGuideCause.value : 'no_results'

      showGuide(cause, 'not_found')
    }

    /**
     * 法人番号を照会できなかった（外部 API 不通）ことを示す
     *
     * @description
     *  「見つからなかった」と同じ扱いにすると、実在する会社なのに「見つかりませんでした」と
     *  嘘をつく（#8982）。パネルが閉じていれば接続エラーとして開く
     */
    const showCorporateNumberUnavailable = () => {
      const cause = searchResultViewCategory.value === 'guide' ? searchGuideCause.value : 'api_error'

      showGuide(cause, 'unavailable')
    }

    /**
     * 直近の検索で得た候補一覧
     *
     * @description
     *  view に持たせず独立させているのは寿命が違うため。サジェストを閉じる際はアコーディオンと
     *  フェードのアニメーション中も候補を描画し続ける必要があり、view より長く生き残る
     */
    const companySearchResults: Ref<CompanySearchResults> = ref([])

    /**
     * 現在の入力値に対して再検索チップで試したクエリ（入力が変わるまで view をまたいで保持する）
     */
    const triedQueries: Ref<string[]> = ref([])

    /**
     * 会社名検索・法人番号照会・会社確定に共通の「最新操作」通し番号
     *
     * @description
     *  3 つの非同期処理はいずれもレスポンスが返る前に次の操作を受け付けるため、古いレスポンスで状態を動かさない仕組みが要る。
     *  どの操作も開始時に startOperation() で世代を進めるので、進行中の他操作（種別を問わず）は自動的に無効になる
     */
    const operationSequence = ref(0)

    /**
     * 新しい操作を開始し、その世代を返す。進行中の操作はすべて無効になる
     */
    const startOperation = () => (operationSequence.value += 1)

    /**
     * 取得した世代が最新でない（＝この処理の実行中に別の操作が始まった）かどうか
     */
    const isStale = (sequence: number) => sequence !== operationSequence.value

    /**
     * 入力された会社名に紐づく会社情報（法人番号等）のうちのいずれかにエラーがあるかどうか
     *
     * @description
     *  会社情報のいずれかにエラーが一つでもある場合は、「会社名」の入力フォームを赤くしてエラー表示扱いにする（「会社名」とそれに紐づく会社情報のバリデーションは本質的に別モノのため、本来は法人番号等がエラーになっても「会社名」は赤くならないため）
     */
    const hasErrorInCompanyInfo = ref(false)

    /**
     * バリデーション開始時点で「会社名」の入力欄に値が埋まっているかどうか
     *
     * @description
     *  - バリデーション開始時点で埋まっている場合は、会社名に紐づく情報（法人番号等）のバリデーションメッセージを表示する（法人番号等が入っていない場合）
     *  - バリデーション開始時点で埋まっていない場合は、会社名に紐づく情報が例え未入力等でバリデーションに引っかかってる状態でも、そちらのメッセージは表示しない（「会社名」の未入力メッセージを優先表示する）
     */
    const isFilledOnValidationStart = ref(false)

    /**
     * 必須会社情報項目
     */
    const requiredItems = computed(() => {
      const _ = [
        props.corporateZipcode,
        props.corporateAddress1,
        props.corporateAddress2,
        props.corporateNumber,
        props.corporateEmployeeNum,
      ]

      return _.filter(v => !!v.validationScheme?.required)
    })

    /**
     * 会社情報のうち、すべての必須項目が入っているかどうか
     */
    const isAllRequiredItemsFulfilled = computed(() => {
      return requiredItems.value.every(v => !!v.value)
    })

    /**
     * 更新状態かどうか（会社名入力欄に変更があれば、会社名以外の情報が揮発する）
     * @see @/models/leadGen.ts
     */
    const isUpdate = computed(() => {
      const _ = [
        props.corporateZipcode,
        props.corporateAddress1,
        props.corporateAddress2,
        props.corporateNumber,
        props.corporateEmployeeNum,
      ]

      return _.every(v => !v.value)
    })

    /**
     * 候補を選ばずに離脱したときに、候補をインラインで再提示すべきか（旧 ModalDefault の表示条件をそのまま流用）
     */
    const shouldKeepResultsVisible = computed<boolean>(() => {
      return !!requiredItems.value.length && !!props.modelValue && !isAllRequiredItemsFulfilled.value && !!companySearchResults.value.length
    })

    /**
     * 「候補に無い会社」として登録済みかどうか（帰結の注記を出す）
     */
    const isUnverified = computed<boolean>(() => props.corporateNumber.value === EXCEPT_OPTION.VALUE)

    // model の backdrop の処理実体を定義
    backdrop(() => {
      closeSuggest()
    }, BACKDROP_CANCEL_ID)

    // バリデーションが発火するタイミングでローカルのエラーフラグも初期化する
    watch(() => validationObserver?.inProgress.value, (newValue) => {
      if (newValue) {
        isFilledOnValidationStart.value = false
        hasErrorInCompanyInfo.value = false
      }
    })

    /**
     * 勤務先を入力したら候補を取得する
     *
     * @description lodash の debounce によって連続入力の場合は処理を待機させ、不要な検索リクエストを避ける
     *              13 桁の数字なら会社名検索ではなく法人番号として照会する（電話番号との誤入力を法人番号側で救う）
     * @param { string } 会社名の入力値
     */
    const onUpdate = debounce(async (str: string) => {
      emit('update:modelValue', str)
      triedQueries.value = []
      // 入力が変わった時点で、飛んでいる会社名検索・法人番号照会のレスポンスはすべて無効にする
      startOperation()

      // 案内は直前のクエリに対する結論なので、クエリが変わったら閉じる。
      // 残すと結果が返る前に新しいクエリで「見つかりませんでした」と断定してしまう
      // （再検索チップ経由の searchCompanies では閉じない。チップ自身が消えるため）
      if (searchResultViewCategory.value === 'guide' || !str) {
        showIdle()
      } else {
        resetViewPayload()
      }

      if (!str) {
        initSearchResult()
        return
      }

      const corporateNumber = extractCorporateNumber(str)
      if (corporateNumber) {
        await lookupCorporateNumber(corporateNumber)
        return
      }

      await searchCompanies(str)
    }, 200)

    /**
     * 会社名で検索し、結果に応じて状態を遷移させる
     *
     * @param query 検索クエリ（再検索チップからは表示値と異なる変換後のクエリが渡る）
     * @param retryQuery 再検索チップ経由のときのクエリ（インライン見出しに使う）
     */
    const searchCompanies = async (query: string, retryQuery?: string) => {
      const sequence = startOperation()
      resetViewPayload()

      let result: CompanySearchResponse
      try {
        result = await fetchCompanies(query)
      } catch {
        if (isStale(sequence)) return

        // 非 2xx・通信例外: 自動で「未設定」にせず、法人番号入力と未設定登録を案内するパネルへ
        showGuide('api_error')
        console.error('Failed to fetch data for companies list.')
        return
      }

      if (isStale(sequence)) return

      // 検索中にサジェストから会社が確定した場合や、入力が消えた後のレスポンスは適用しない
      if (!isUpdate.value || !props.modelValue) return

      // 外部 API が一次・二次とも不通。候補 0 件と同じ案内を出すと「見つかりませんでした」と
      // 嘘をつき、ユーザーは会社名を手入力して「未設定」のまま登録を終える（#8982）
      if (!result.available) {
        showGuide('api_error')
        return
      }

      companySearchResults.value = result.companies

      // 再検索チップのクエリは結果が出てから表示値に反映する。
      // 先に反映すると、案内の見出しがまだ検索していないクエリで「見つかりませんでした」と断定してしまう
      if (retryQuery) emit('update:modelValue', retryQuery)

      if (!companySearchResults.value.length) {
        showGuide('no_results')
        return
      }

      if (retryQuery) {
        showInlineResults(`「${retryQuery}」で${companySearchResults.value.length}件見つかりました。候補から選んでください`)
        return
      }

      if (isFocus.value) {
        initSuggestPosition()
        showSuggest()
        return
      }

      if (shouldKeepResultsVisible.value) {
        showInlineResults(UNSELECTED_HEADING)
        return
      }

      showIdle()
    }

    /**
     * フォーカスがコンポーネントの外へ出たら浮動サジェストを閉じる
     *
     * @description
     *  閉じる判定を backdrop と同じ「操作がコンポーネント内かどうか」に揃えるための処理。
     *  入力欄の blur ではフォーカスの移動先が分からないため、バブリングして relatedTarget に
     *  移動先が入る focusout をルート要素で受ける（blur 直後の document.activeElement は
     *  一時的に body を指すため判定に使えない）。
     *  サジェスト内の都道府県ラジオへフォーカスが移った場合に閉じないのはこの判定による。
     *
     *  relatedTarget が null のときは閉じない。これは「外へ出た」ではなく「移動先が分からない」を
     *  意味するため。フォーカスを受け取れない要素（都道府県チップの label など）を押した場合や、
     *  ボタンをクリックしてもフォーカスを与えない Safari がこれに当たり、閉じてしまうと
     *  候補を選ぶ前にサジェストが消える。このケースはクリック位置を DOM で判定できる
     *  backdrop（data-backdrop-cancel）が担当するため、ここでは判定を委ねる
     */
    const onFocusOut = (event: FocusEvent) => {
      if (!event.relatedTarget) return
      if (rootEl.value?.contains(event.relatedTarget as Node)) return

      closeSuggest()
    }

    /**
     * 浮動サジェストを閉じる。候補未選択のまま離脱したら同じ候補をインラインに出す（旧 ModalDefault の役割）
     */
    const closeSuggest = () => {
      if (searchResultViewCategory.value !== 'suggest') return

      if (shouldKeepResultsVisible.value) {
        showInlineResults(UNSELECTED_HEADING)
        return
      }

      showIdle()
    }

    /**
     * 検索・照会 API のレスポンスを現行形に揃える
     *
     * @description
     *  デプロイ中は新しい JS が旧 API を叩く瞬間がある。旧形は検索が裸の配列、照会が会社情報
     *  そのもので、いずれも available を持たない。欠落を「不通」と見なすと、その間の全操作が
     *  「接続できませんでした」になる（#8982）
     * @todo #8982 本番リリース後、旧 API を返すタスクが居なくなったら削除する
     */
    const normalizeSearchResponse = (data: CompanySearchResponse | CompanySearchResults): CompanySearchResponse => {
      if (Array.isArray(data)) return { available: true, companies: data }

      return data
    }

    const normalizeLookupResponse = (data: CompanyLookupResponse | CompanySearchInfo): CompanyLookupResponse => {
      if ('available' in data) return data

      return { available: true, company: data }
    }

    /**
     * 勤務先企業名の候補一覧を取得する
     *
     * @description 取得するだけで状態は動かさない。古いレスポンスの破棄と結果の格納は呼び出し側（searchCompanies）が行う
     * @returns 検索 API が返した候補一覧と、検索できたかどうか（available・#8982）
     * @throws 非 2xx・通信例外のとき（呼び出し側で api_error として扱う）
     */
    const fetchCompanies = async (companyName: string): Promise<CompanySearchResponse> => {
      return await new Promise((resolve, reject) => {
        if (!companyName) {
          reject()

          return
        }

        const params = {
          // 会社名検索の読み仮名検索はカタカナ前提（ひらがな入力では候補がヒットしない）のため、検索クエリをカタカナに正規化する。
          // ただし漢字やカタカナが混じると正規化がかえって検索ヒットを妨げるため、ひらがなのみの入力時だけ変換する。
          // 表示中の入力値（modelValue）は書き換えないため、ユーザーの入力体験は変えない。
          company_name: isHiraganaOnly(companyName) ? formatHiraganaToKatakana(companyName) : companyName,
        }

        try {
          apiRequestManager.handleRequest({
            endpoint: COMPANY.SEARCH,
            params,
            callback: (response, isSuccess) => {
              if (isSuccess) {
                resolve(normalizeSearchResponse(response.data))
                return
              }
              reject()
            }
          })
        } catch (error) {
          console.error(error)
          reject()
        }
      })
    }

    /**
     * 法人番号（13桁）から会社を照会し、200 + 会社情報なら候補選択と同じ経路で確定する
     *
     * @description 実行回（operationSequence）が最終回と一致しないレスポンスは破棄する。
     *              照会中にユーザーが入力を変えたり別の番号を入れた場合に、
     *              古い成功で勝手に会社が確定したり、古い未発見応答で確定済みの会社の上にパネルが開くのを防ぐ。
     *              未発見・外部 API 失敗は共通エラー処理を避けるため 200。未発見は company: null、
     *              不通は available: false で区別する（#8982）。非 2xx も防御的に未発見として扱う
     */
    const lookupCorporateNumber = async (corporateNumber: string) => {
      const sequence = startOperation()
      updateCorporateNumberLookupStatus('loading')

      try {
        await apiRequestManager.handleRequest({
          endpoint: COMPANY.LOOKUP_BY_CORPORATE_NUMBER,
          params: { corporate_number: corporateNumber },
          callback: (response, isSuccess) => {
            if (isStale(sequence)) return

            if (isSuccess && response.data) {
              const { available, company } = normalizeLookupResponse(response.data)

              // 外部 API が不通。未発見と同じ扱いにすると、案内パネルが勧めた逃げ道が
              // 「この番号の会社は見つかりませんでした」と嘘をつく（#8982）
              if (!available) {
                showCorporateNumberUnavailable()
                return
              }

              if (company) {
                // 既に会社が確定している（更新状態でない）場合は、このレスポンスで上書きしない
                if (!isUpdate.value) return

                updateCorporateNumberLookupStatus('idle')
                selectCompany(company)
                return
              }
            }
            showCorporateNumberNotFound()
          }
        })
      } catch (error) {
        console.error(error)
        if (isStale(sequence)) return

        showCorporateNumberNotFound()
      }
    }

    /**
     * 再検索チップ: 変換後のクエリで検索し直し、結果が返ってから入力欄も揃える
     *
     * @description
     *  自動正規化（ひらがな→カタカナ・#8264）は表示値を書き換えないが、チップはユーザーが
     *  「「〇〇」で探す」と書かれたボタンを押す明示的な操作のため、入力欄が追随しないと
     *  「入力欄の会社名」と「一覧・パネルが指す会社名」が食い違う。
     */
    const retrySearch = async (query: string) => {
      triedQueries.value = [...triedQueries.value, query]
      await searchCompanies(query, query)
    }

    /**
     * 候補一覧の「別の探し方」から、案内パネルを開く
     */
    const openGuide = async () => {
      startOperation()
      showGuide('user_initiated')

      // パネルは v-if なので、描画されるまで待たないとスクロール先の要素が存在しない
      await nextTick()
      scrollToHash(guidePanelId)
    }

    /**
     * 選択された会社の従業員数情報を取得
     *
     * @param companySearchInfo 選択された会社情報
     */
    const fetchEmployeeNum = async (companySearchInfo: CompanySearchInfo): Promise<number | null | undefined> => {
      let employeeNum: number | null | undefined
      const params = {
        corporate_number: companySearchInfo.corporate_number,
      }
      try {
        await apiRequestManager.handleRequest({
          endpoint: EMPLOYEE_NUM,
          params,
          callback: (response, isSuccess) => {
            if (isSuccess) {
              employeeNum = response.data
            }
          }
        })
      } catch (error) {
        console.error(error)
      }

      return employeeNum
    }

    /**
     * 候補（サジェスト・インライン一覧・法人番号照会）から会社を確定した際の処理
     * @param companySearchInfo 選択した会社情報
     */
    const selectCompany = async (companySearchInfo: CompanySearchInfo) => {
      const sequence = startOperation()

      let employeeNum: number | null | undefined
      // 法人番号が入っているときだけ従業員数の情報取得 API も投げる
      if (companySearchInfo.corporate_number) {
        employeeNum = await fetchEmployeeNum(companySearchInfo)
      }
      if (isStale(sequence)) return

      // 企業情報取得 API の結果において、企業によっては法人番号が抜けてるものがあり、
      // ユーザが企業を選択した状態なのか、それとも会社名フリー入力のみで未選択のままのユーザなのかの区別がつかないため、
      // 選択された企業情報の法人番号が null のものは、便宜的に「未設定」の値を法人番号に入れる
      const _companySearchInfo = JSON.parse(JSON.stringify(companySearchInfo))
      if (!_companySearchInfo.corporate_number) {
        _companySearchInfo.corporate_number = EXCEPT_OPTION.VALUE
      }

      showIdle()

      emit('select:companyLeadInfo', { ..._companySearchInfo, ...{ employee_num: employeeNum } })
      initSearchResult()
    }

    /**
     * 「候補に無い会社として登録する」を選んだ場合、便宜的に法人番号を「未設定」で送る（送信値は従来の「該当なし」と同一）
     */
    const selectExceptOption = () => {
      selectCompany({
        address: props.corporateAddress1.validationScheme?.required ? EXCEPT_OPTION.VALUE : '',
        company_name: props.modelValue,
        corporate_number: EXCEPT_OPTION.VALUE,
        zipcode: '',
        employee_num: '',
      })
    }

    /**
     * サジェスト表示位置の初期化
     * @description サジェストの高さが画面領域から溢れる場合は、「会社名」入力フォームの上部にサジェストを表示する。（通常はフォーム下部に表示）
     */
    const initSuggestPosition = () => {
      const SUGGEST_MAX_HEIGHT = 300 as const

      // サジェストのボタン 1 つの高さ（ブラウザ環境で誤差は出るので、大まかな概算値）
      const SUGGEST_BUTTON_HEIGHT = 60

      // 入力フォームの画面表示位置
      const ROOT_POSITION = rootEl.value.getBoundingClientRect().top
      const INPUT_POSITION = inputEl.value.$el.getBoundingClientRect().top

      // サジェスト全体の高さ
      const suggestHeight = SUGGEST_MAX_HEIGHT < companySearchResults.value.length * SUGGEST_BUTTON_HEIGHT
        ? SUGGEST_MAX_HEIGHT
        : companySearchResults.value.length * SUGGEST_BUTTON_HEIGHT

      suggestPositionStyle.value = document.documentElement.clientHeight > (INPUT_POSITION + inputEl.value.$el.offsetHeight + suggestHeight)
        ? `top: ${(INPUT_POSITION + inputEl.value.$el.offsetHeight) - ROOT_POSITION}px`
        : `bottom: ${inputEl.value.$el.offsetHeight}px`
    }

    /**
     * サジェストのアコーディオン処理が完了した際のコールバック処理（emit で完了通知を受け取る）
     *
     * @description サジェストが閉じられた際に、会社名を選択されていれば（法人番号が入っていれば）検索結果をリセットする
     */
    const accordionTransitionEnd = () => {
      if (searchResultViewCategory.value !== 'suggest' && !!props.corporateNumber.value) initSearchResult()
    }

    const initSearchResult = () => {
      companySearchResults.value = []
    }

    /**
     * window のリサイズイベントを監視して、幅に変更があったらサジェストの表示位置を再取得
     */
    const resizeListener = throttle(async () => {
      if (searchResultViewCategory.value === 'suggest') {
        initSuggestPosition()
      }
    }, 100)

    const cleanupScroll = scrollUtil.throttle({
      callback: () => {
        if (searchResultViewCategory.value === 'suggest') {
          initSuggestPosition()
        }
      },
      wait: 30,
    })

    window.addEventListener('resize', resizeListener)

    onBeforeUnmount(() => {
      // イベントリスナー解除
      cleanupScroll()
      window.removeEventListener('resize', resizeListener)
    })

    return {
      // constants
      VALIDATION_DEFAULT_MESSAGES,
      COMPANY_INFO_REQUIRED_MESSAGE,
      BACKDROP_CANCEL_ID,

      // data
      searchResultViewCategory,
      inlineHeading,
      searchGuideCause,
      corporateNumberLookupStatus,
      rootEl,
      inputEl,
      guidePanelId,
      suggestPositionStyle,
      companySearchResults,
      hasErrorInCompanyInfo,
      isFilledOnValidationStart,
      isFocus,
      triedQueries,

      // computed
      hasError,
      isRequired,
      isUnverified,

      // methods
      createErrorMessage,
      onUpdate,
      onFocusOut,
      selectCompany,
      selectExceptOption,
      retrySearch,
      lookupCorporateNumber,
      openGuide,
      accordionTransitionEnd,
    }
  }
})
</script>

<template>
  <div
    ref="rootEl"
    class="o-list__inputWithSuggest"
    @focusout="onFocusOut">
    <dl class="o-list__inputWithSuggest--unit">
      <dt class="o-list__inputWithSuggest--term u-text__bold u-text__small__fixed">
        {{ header }}
        <span
          v-if="isRequired"
          class="span u-text__required">
          必須
        </span>

        <div class="o-note u-text__small u-text__bold u-margin__smaller">
          <p class="o-note--item">
            会社名の一部でも検索できます。候補から選ぶと、業種に合ったおすすめコンテンツが届きます。
          </p>
        <!-- /.o-note --></div>
      </dt>
      <dd
        class="o-list__inputWithSuggest--description is-input"
        :data-backdrop-cancel="BACKDROP_CANCEL_ID">
        <ValidationProvider
          :value="modelValue"
          :namespace="namespace"
          :field="name"
          :scheme="validationScheme"
          :messages="createErrorMessage(false, false, header)"
          @start="isFilledOnValidationStart = !!modelValue"
          @error="$emit('error', $event)">
          <InputDefault
            ref="inputEl"
            :model-value="modelValue"
            :name="name"
            :placeholder="placeholder"
            :autocomplete="autocomplete"
            :has-trim="hasTrim"
            :is-disabled="isDisabled"
            :is-error="hasError || hasErrorInCompanyInfo"
            @update:model-value="onUpdate($event)"
            @trim="$emit('update:modelValue', $event)"
            @blur="isFocus = false"
            @focus="isFocus = true" />
        </ValidationProvider>

        <!--
          勤務先郵便番号
        -->
        <ValidationProvider
          v-if="corporateZipcode"
          :value="corporateZipcode.value"
          :namespace="namespace"
          :field="corporateZipcode.name"
          :scheme="corporateZipcode.validationScheme"
          :messages="{
            ...VALIDATION_DEFAULT_MESSAGES,
            required: COMPANY_INFO_REQUIRED_MESSAGE,
          }"
          :show-message="isFilledOnValidationStart"
          @start="hasErrorInCompanyInfo = false"
          @error="hasErrorInCompanyInfo = true">
          <input
            class="input"
            type="hidden"
            :name="corporateZipcode.name"
            :value="corporateZipcode.value">
        </ValidationProvider>

        <!--
          勤務先住所 1
        -->
        <ValidationProvider
          v-if="corporateAddress1"
          :value="corporateAddress1.value"
          :namespace="namespace"
          :field="corporateAddress1.name"
          :scheme="corporateAddress1.validationScheme"
          :messages="{
            ...VALIDATION_DEFAULT_MESSAGES,
            required: COMPANY_INFO_REQUIRED_MESSAGE,
          }"
          :show-message="isFilledOnValidationStart"
          @start="hasErrorInCompanyInfo = false"
          @error="hasErrorInCompanyInfo = true">
          <input
            class="input"
            type="hidden"
            :name="corporateAddress1.name"
            :value="corporateAddress1.value">
        </ValidationProvider>

        <!--
          勤務先住所 2
        -->
        <ValidationProvider
          v-if="corporateAddress2"
          :value="corporateAddress2.value"
          :namespace="namespace"
          :field="corporateAddress2.name"
          :scheme="corporateAddress2.validationScheme"
          :messages="{
            ...VALIDATION_DEFAULT_MESSAGES,
            required: COMPANY_INFO_REQUIRED_MESSAGE,
          }"
          :show-message="isFilledOnValidationStart"
          @start="hasErrorInCompanyInfo = false"
          @error="hasErrorInCompanyInfo = true">
          <input
            class="input"
            type="hidden"
            :name="corporateAddress2.name"
            :value="corporateAddress2.value">
        </ValidationProvider>

        <!--
          法人番号

          「会社名」の入力値があり、且つ「法人番号」が null だった場合、「法人番号」用のバリデーションエラーメッセージを表示
          「会社名」の入力値がなかった場合、（「法人番号」の値の有無に関わらず）「法人番号」用のエラーメッセージは出さず、「会社名」用のエラーのみ表示
        -->
        <ValidationProvider
          v-if="corporateNumber"
          :value="corporateNumber.value"
          :namespace="namespace"
          :field="corporateNumber.name"
          :scheme="corporateNumber.validationScheme"
          :messages="{
            ...VALIDATION_DEFAULT_MESSAGES,
            required: COMPANY_INFO_REQUIRED_MESSAGE,
          }"
          :show-message="isFilledOnValidationStart"
          @start="hasErrorInCompanyInfo = false"
          @error="hasErrorInCompanyInfo = true">
          <input
            class="input"
            type="hidden"
            :name="corporateNumber.name"
            :value="corporateNumber.value">
        </ValidationProvider>

        <!--
          従業員数
        -->
        <ValidationProvider
          v-if="corporateEmployeeNum"
          :value="corporateEmployeeNum.value"
          :namespace="namespace"
          :field="corporateEmployeeNum.name"
          :scheme="corporateEmployeeNum.validationScheme"
          :messages="{
            ...VALIDATION_DEFAULT_MESSAGES,
            required: COMPANY_INFO_REQUIRED_MESSAGE,
          }"
          :show-message="isFilledOnValidationStart"
          @start="hasErrorInCompanyInfo = false"
          @error="hasErrorInCompanyInfo = true">
          <input
            class="input"
            type="hidden"
            :name="corporateEmployeeNum.name"
            :value="corporateEmployeeNum.value">
        </ValidationProvider>

        <!-- 候補に無い会社として登録済みのときの帰結の注記 -->
        <div
          v-if="isUnverified"
          class="o-note__general u-text__small u-margin__smaller">
          <p class="o-note__general--item">候補に無い会社として登録します。業種に合ったおすすめコンテンツは届きません。</p>
        <!-- /.o-note__general --></div>
      </dd>

      <!-- 検索結果の浮動 suggest 表示（フォーカス中） -->
      <!-- サジェスト内（都道府県チップ等）のクリックを backdrop の閉じ判定から除外する -->
      <transition name="is-fade">
        <dd
          v-show="searchResultViewCategory === 'suggest'"
          class="o-list__inputWithSuggest--description is-suggest"
          :style="suggestPositionStyle"
          :data-backdrop-cancel="BACKDROP_CANCEL_ID">
          <AccordionBody
            :open-state="searchResultViewCategory === 'suggest'"
            unit-of-visible-height="px"
            @transitionend="accordionTransitionEnd">
            <CompanySearchResultList
              :results="companySearchResults"
              @select="selectCompany"
              @click:guide-link="openGuide" />
          </AccordionBody>
        </dd>
      </transition>

      <!-- 候補を選ばずに離脱した／再検索チップで候補が出たときのインライン一覧（旧・強制選択モーダルの代替） -->
      <dd
        v-if="searchResultViewCategory === 'inlineResults'"
        class="o-list__inputWithSuggest--description is-inlineResults">
        <CompanySearchResultList
          :results="companySearchResults"
          @select="selectCompany"
          @click:guide-link="openGuide">
          <template #heading>
            {{ inlineHeading }}
          </template>
        </CompanySearchResultList>
      </dd>

      <!-- 候補 0 件・API エラーのときの見つからないときパネル -->
      <dd
        v-if="searchResultViewCategory === 'guide'"
        :id="guidePanelId"
        class="o-list__inputWithSuggest--description is-guide">
        <CompanySearchGuidePanel
          :query="modelValue"
          :cause="searchGuideCause"
          :tried-queries="triedQueries"
          :corporate-number-lookup-status="corporateNumberLookupStatus"
          @click:retry-query="retrySearch"
          @fill:corporate-number="lookupCorporateNumber"
          @click:not-applicable="selectExceptOption" />
      </dd>
    </dl>
  <!-- /.o-list__inputWithSuggest --></div>
</template>

<style lang="sass" scoped>
.o-list__inputWithSuggest
  & &--unit
    position: relative
    margin-top: 1.7em

    &:first-child
      margin-top: 0

  & &--term
    margin-top: 1.7em

    &:first-child
      margin-top: 0

  & &--description
    &.is-input
      margin-top: 0.25em

      &:first-child
        margin-top: 0

    &.is-suggest
      width: 100%
      max-height: 300px
      position: absolute
      overflow-y: auto
      background-color: rgba($bg-xx-light-cyan, 1)
      border: 1px solid $border-xx-light-cyan
      border-radius: $border-radius
      box-shadow: $box-shadow
      z-index: 2

    // 候補が数十件返ることがあるため、浮動サジェストと同じ高さで内部スクロールさせ、以降の入力欄を押し下げない
    &.is-inlineResults
      max-height: 300px
      overflow-y: auto
      margin-top: 0.5em
      background-color: rgba($bg-xx-light-cyan, 1)
      border: 1px solid $border-xx-light-cyan
      border-radius: $border-radius

    &.is-guide
      margin-top: 0.5em
</style>
